
import { Component, Vue } from "vue-property-decorator"
import { namespace, Getter, Action, State } from "vuex-class"

import { Case, Team } from "@/types"
import { ViewFullSummary } from "@/types/views/viewSummary"
import User from "@/types/user"

import CaseList from "@/components/CaseList/CaseList.vue"
import CaseFilter from "@/components/CaseList/CaseFilter.vue"
import CaseFilters from "@/components/CaseList/CaseFilters.vue"
import SkeletonLoader from "@/components/SkeletonLoader.vue"
import RetractableSidebar from "@/components/RetractableSidebar.vue"

import vSelect from "vue-select"
// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"

import { ViewsService } from "@/services/viewsService"
import PageWrapper from "@/components/New/Page/PageWrapper.vue"
import api from "@/services/api"
import { CurrentPageDetails, GetCasesForViewRequest } from "@/types/paginatedRequest"
import { CasesTable } from "@/types/case"
import DataCache from "@/caching/caching"

const caseStore = namespace("cases")
const setupStore = namespace("setup")

@Component({
	components: {
		"case-list": CaseList,
		"page": PageWrapper,
		"case-filter": CaseFilter,
		"case-filters": CaseFilters,
		"v-select": vSelect,
		"scrollbar": VuePerfectScrollbar,
		"skeleton-loader": SkeletonLoader,
		"retractable-sidebar": RetractableSidebar
	}
})
export default class CasesOverview extends Vue {
	@State("userCache") private readonly userCache!: DataCache<User>
	@Getter("getMe") public me!: User;
	@Getter("getScreenWidth") public screenWidth!: number;
	@Getter("getCaseName") getCaseName!: string;
	@Getter("getRecordName") getRecordName!: string;
	@Getter("getTeams") public teams!: Team[];
	@Action public loadTeams!: () => void

	@caseStore.State viewsService!: ViewsService;
	@caseStore.Action public casesLoad!: () => void;
	@caseStore.Action public caseClear!: () => void;
	@setupStore.Action public loadFieldsIfUnloaded!: () => Promise<void>;

	@caseStore.Mutation("setActiveViewId") public setActiveViewId!: (viewId: string | null) => void;

	@caseStore.Action("detailsLoad")
	private detailsLoad!: () => void;

	private casesTables: CasesTable[] = [];
	private loadingPage: boolean = false;
	private cacheId: string = "";

	private get viewSummary (): ViewFullSummary | null {
		return this.viewsService.viewSummary
	}

	public async mounted () {
		this.caseClear()
		this.detailsLoad()
		const loadUsersPromise = this.userCache.fetchIfNeeded()
		const loadFieldsPromise = this.loadFieldsIfUnloaded()
		const loadViewsPromise = this.setCases()
		await Promise.allSettled([loadUsersPromise, loadFieldsPromise, loadViewsPromise])
	}

	public async changeView (viewId: string | null) {
		this.viewsService.activeViewId = viewId
		this.setCases()
	}

	public async setCases (searchTerm: string = ""): Promise<void> {
		await this.viewsService.loadViews(searchTerm, this.cacheId)
		this.casesTables = this.viewsService.viewSummary?.casesTables ?? []
		this.cacheId = this.viewsService.viewSummary?.id ?? ""
	}

	private async loadNextPage (details: CurrentPageDetails) {
		if (this.viewsService.activeViewId) {
			try {
				this.loadingPage = true
				const request: GetCasesForViewRequest = {
					viewId: this.viewsService.activeViewId,
					page: details.pageNumber ?? 1,
					pageSize: details.pageSize ?? 20,
					searchTerm: details.searchTerm,
					timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
					sortBy: details.sortBy,
					sortDesc: details.sortDesc,
					sortDataType: details.sortDataType,
					tableId: details.tableId,
					id: this.cacheId
				}

				const tableId = details.tableId ? details.tableId : details.value
				const cases = await api.casesForView(request)
				const tableToUpdate = this.casesTables.firstOrDefault((c: CasesTable) => c.value === tableId)

				if (tableToUpdate) {
					tableToUpdate.totalRecords = cases.totalItems
					tableToUpdate.cases = [...cases.data]
				}
			} catch (err) {
				console.error(err)
				this.setCases()
			} finally {
				this.loadingPage = false
			}
		}
	}

	private async reloadInsightBySearch (searchTerm: string): Promise<void> {
		if (this.viewsService.activeViewId) {
			try {
				this.loadingPage = true
				await this.setCases(searchTerm)
			} catch (err) {
				console.error(err)
				this.setCases()
			} finally {
				this.loadingPage = false
			}
		}
	}
}

